// ─────────────────────────────────────────────────────────────────────
// WHERE AM I SIGNED IN, AND «SIGN OUT EVERYWHERE ELSE» — the browser half
// (#1141). The routes are `GET /me/sessions` and
// `POST /me/sessions/revoke-others` (`apps/api/src/routes/mySessions.ts`).
//
// The screen needs one number — how many OTHER places this account is signed
// in — and one action. A session carries no device, so a per-row list would
// ask the person to choose between rows they cannot tell apart.
// ─────────────────────────────────────────────────────────────────────
import { apiFetch } from './http';

export type MySessionsOutcome = { status: 'ok'; others: number } | { status: 'unreachable' };

export type RevokeOthersOutcome =
  | { status: 'revoked'; count: number }
  | { status: 'reauthentication_required' }
  | { status: 'unreachable' };

/** How many OTHER places this account is signed in: sign-ins plus member-app sessions. */
export async function fetchMySessions(): Promise<MySessionsOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/me/sessions');
  } catch {
    return { status: 'unreachable' };
  }
  if (!res.ok) return { status: 'unreachable' };
  const body = (await res.json().catch(() => null)) as {
    sessions?: { current?: unknown }[];
    memberSessions?: unknown;
  } | null;
  if (!Array.isArray(body?.sessions) || typeof body?.memberSessions !== 'number') {
    return { status: 'unreachable' };
  }
  const otherSignIns = body.sessions.filter((s) => s?.current !== true).length;
  return { status: 'ok', others: otherSignIns + body.memberSessions };
}

/** End every other sign-in. Needs a sign-in from the last fifteen minutes. */
export async function revokeOtherSessions(): Promise<RevokeOthersOutcome> {
  let res: Response;
  try {
    res = await apiFetch('/me/sessions/revoke-others', { method: 'POST' });
  } catch {
    return { status: 'unreachable' };
  }
  if (res.status === 403) {
    const source = typeof res.clone === 'function' ? res.clone() : res;
    const body = (await source.json().catch(() => null)) as { error?: unknown } | null;
    if (body?.error === 'reauthentication_required') return { status: 'reauthentication_required' };
  }
  if (!res.ok) return { status: 'unreachable' };
  const body = (await res.json().catch(() => null)) as {
    revoked?: { sessions?: unknown; memberSessions?: unknown };
  } | null;
  const s = Number(body?.revoked?.sessions ?? 0);
  const m = Number(body?.revoked?.memberSessions ?? 0);
  return { status: 'revoked', count: (Number.isFinite(s) ? s : 0) + (Number.isFinite(m) ? m : 0) };
}
